// Actions
import { ActionType } from "@ms/uno-actions/lib/local/ActionType";
// Constants
import { StoreTableNames, StoreEntityStatus, MruStoreCollectionKey } from "@ms/uno-constants/lib/local/StoreConstants";
import { Entity } from "@ms/uno-entitycore/lib/local/Entity";
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
import { BasicTable } from "@ms/uno-entitycore/lib/local/tables/BasicTable";
import { EntityTable } from "@ms/uno-entitycore/lib/local/tables/EntityTable";
import { ProjectMetadataOrgType } from "@ms/uno-models/lib/local/constants/MruConstants";
import { MruApp } from "@ms/uno-models/lib/local/service/mru/MruApp";
import { MruSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/mru/MruItemSerializationHandler";
import { StoreEntityStatusSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyStatus/StoreEntityStatusSerializationHandler";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import isEqual from "lodash/isEqual";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { getStatusBasedOnResponse } from "../../utilities/StatusUtilities";
import { Temporal } from "temporal-polyfill-lite";
/**
 * Store for MRU data
 */ export class MruStore extends EventPublishingStore {
    get name() {
        return "MruStore";
    }
    getMruItemById(mruId) {
        const itemsById = Object.values(this.mruTable.getByIndex({
            name: "mruId",
            value: mruId
        }));
        return itemsById.length > 0 ? itemsById[0].value : null;
    }
    getMruItemByServerDocId(serverDocId, readBaseline) {
        const entity = this.mruTable.get(serverDocId);
        return entity && entity.status === ExistenceStatus.Created ? readBaseline ? entity.baseline : entity.value : null;
    }
    getRecentItems() {
        return {
            data: Object.values(this.mruTable.getByIndex({
                name: "visible",
                value: "true"
            })).map((entity)=>entity.value),
            status: this.mruCollectionStatusTable.get(this.mruCollectionKey) ?? StoreEntityStatus.Unfetched,
            isFetching: this.isMruFetchingInProgress[this.mruCollectionKey] ?? false
        };
    }
    getPinnedItems() {
        return {
            data: Object.values(this.mruTable.getByIndex({
                name: "visiblePinned",
                value: "true"
            })).map((entity)=>entity.value),
            status: this.mruCollectionStatusTable.get(this.mruCollectionKey) ?? StoreEntityStatus.Unfetched,
            isFetching: this.isMruFetchingInProgress[this.mruCollectionKey] ?? false
        };
    }
    getPersistedTables() {
        return {
            [StoreTableNames.Mru]: this.mruTable,
            [StoreTableNames.MruCollectionStatus]: this.mruCollectionStatusTable
        };
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchMruItems:
                storeChanged = this.handleFetchMruItemsAction(action);
                break;
            case ActionType.FetchMruItemsFailed:
                storeChanged = this.handleFetchMruItemsFailedAction(action);
                break;
            case ActionType.FetchMruItemsSucceeded:
                storeChanged = this.handleFetchMruItemsSucceededAction(action);
                break;
            case ActionType.CreateMruItem:
                storeChanged = this.handleCreateMruItemAction(action);
                break;
            case ActionType.CreateMruItemFailed:
                storeChanged = this.handleCreateMruItemFailedAction(action);
                break;
            case ActionType.CreateMruItemSucceeded:
                storeChanged = this.handleCreateMruItemSucceededAction(action);
                break;
            case ActionType.UpdateMruItem:
                storeChanged = this.handleUpdateMruItemAction(action);
                break;
            case ActionType.UpdateMruItemFailed:
                storeChanged = this.handleUpdateMruItemFailedAction(action);
                break;
            case ActionType.UpdateMruItemSucceeded:
                storeChanged = this.handleUpdateMruItemSucceededAction(action);
                break;
            case ActionType.DeleteMruItem:
                storeChanged = this.handleDeleteMruItemAction(action);
                break;
            case ActionType.DeleteMruItemFailed:
                storeChanged = this.handleDeleteMruItemFailedAction(action);
                break;
            case ActionType.DeleteMruItemSucceeded:
                storeChanged = this.handleDeleteMruItemSucceededAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                break;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleFetchMruItemsAction(action) {
        if (this.isMruFetchingInProgress[this.mruCollectionKey]) {
            return false;
        }
        this.isMruFetchingInProgress[this.mruCollectionKey] = true;
        return true;
    }
    handleFetchMruItemsFailedAction(action) {
        const { response } = action;
        let storeChanged = false;
        const status = response != null ? getStatusBasedOnResponse(response) ?? StoreEntityStatus.UnableToFetch : StoreEntityStatus.UnableToFetch;
        if (this.isMruFetchingInProgress[this.mruCollectionKey]) {
            this.isMruFetchingInProgress[this.mruCollectionKey] = false;
            storeChanged = true;
        }
        if (status && this.mruCollectionStatusTable.get(this.mruCollectionKey) !== status) {
            this.mruCollectionStatusTable.set(this.mruCollectionKey, status);
            return true;
        }
        return storeChanged;
    }
    handleFetchMruItemsSucceededAction(action) {
        let storeChanged = false;
        if (this.isMruFetchingInProgress[this.mruCollectionKey]) {
            this.isMruFetchingInProgress[this.mruCollectionKey] = false;
            storeChanged = true;
        }
        for (const mruItem of action.mruItems){
            const storeMruItem = this.mruTable.get(mruItem.serverDocId);
            const isCreatedMruItem = this.createdMruItems[mruItem.serverDocId] != null;
            if (storeMruItem == null && !this.mruRecentlyDeletedItems.has(mruItem.serverDocId)) {
                this.mruTable.add(mruItem.serverDocId, mruItem);
                storeChanged = true;
            } else if (storeMruItem && Temporal.Instant.compare(storeMruItem.baseline.timestamp, mruItem.timestamp) < 0) {
                storeMruItem.updateBaseline(mruItem);
                storeChanged = true;
            } else if (storeMruItem) {
                let reconciledItem = storeMruItem.baseline;
                let shouldUpdateBaseline = false;
                // mru_id is minted by the service, so a fetched value is authoritative even when timestamp
                // reconciliation keeps newer local fields from an optimistic write.
                if (mruItem.mruId !== undefined && mruItem.mruId !== reconciledItem.mruId) {
                    reconciledItem = reconciledItem.setProperty("mruId", mruItem.mruId);
                    shouldUpdateBaseline = true;
                }
                if (mruItem.isPinned !== reconciledItem.isPinned && (Temporal.Instant.compare(reconciledItem.timestamp, mruItem.timestamp) === 0 || isCreatedMruItem)) {
                    // If the item was fetched and the fetched item has a different pinned state, update the store copy
                    // This can happen if the fetch happens after the create has finished but has the latest data from the create (new timestamp)
                    // client sends create
                    // client gets back create response with the latest timestamp
                    // client sends fetch
                    // client gets back fetch where the item is pinned and has new timestamp
                    // If the item was created locally and the fetched item has a different pinned state, update the store copy
                    // Another case can also happen if the fetch happens before the create has finished
                    // client sends fetch
                    // client sends create
                    // client gets back fetch where the item is pinned but has an old timestamp (fetch doesn't have latest create timestamp)
                    // client gets back create response with the latest timestamp
                    // In this case, the create has the latest data but is missing pinned state, so only sync pinned state to the baseline
                    reconciledItem = reconciledItem.setProperty("isPinned", mruItem.isPinned);
                    shouldUpdateBaseline = true;
                }
                if (shouldUpdateBaseline) {
                    storeMruItem.updateBaseline(reconciledItem);
                    storeChanged = true;
                }
            }
        }
        // Delete store entries not present in response
        for (const serverDocId of Object.keys(this.mruTable.getAll())){
            if (action.mruItems.find((mruItem)=>mruItem.serverDocId === serverDocId) == null) {
                storeChanged = this.mruTable.delete(serverDocId) || storeChanged;
            }
        }
        // Update the collection status
        if (this.mruCollectionStatusTable.get(this.mruCollectionKey) !== StoreEntityStatus.Present) {
            this.mruCollectionStatusTable.set(this.mruCollectionKey, StoreEntityStatus.Present);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleCreateMruItemAction(action) {
        const { mruItem } = action;
        this.mruTable.add(mruItem.serverDocId, mruItem);
        this.createdMruItems[mruItem.serverDocId] = new Entity(mruItem);
        return true;
    }
    handleCreateMruItemFailedAction(action) {
        const { mruItem } = action;
        const storeCopy = this.mruTable.get(mruItem.serverDocId);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e45e659 /* tag_4r4zz */ , TraceLevel.Warning, `MruItemNotFoundInStore [Action=${action.type}][ServerDocId=${mruItem.serverDocId}]`);
            return false;
        }
        this.mruTable.delete(storeCopy.baseline.serverDocId);
        delete this.createdMruItems[storeCopy.baseline.serverDocId];
        return true;
    }
    handleCreateMruItemSucceededAction(action) {
        const { mruItem } = action;
        const storeCopy = this.mruTable.get(mruItem.serverDocId);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e45e658 /* tag_4r4zy */ , TraceLevel.Warning, `MruItemNotFoundInStore [Action=${action.type}][ServerDocId=${mruItem.serverDocId}]`);
            return false;
        }
        let newItem = mruItem;
        // If fetch came back after create was started, update the store copy with the fetched item pinned state
        if (mruItem.isPinned !== storeCopy.baseline.isPinned) {
            newItem = mruItem.setProperty("isPinned", storeCopy.baseline.isPinned);
        }
        // Update the store tables with the server copy
        storeCopy.updateBaseline(newItem);
        this.createdMruItems[mruItem.serverDocId] = storeCopy;
        return true;
    }
    handleUpdateMruItemAction(action) {
        let storeChanged = false;
        const { serverDocId, delta, updateKey } = action;
        const storeCopy = this.mruTable.get(serverDocId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e45e654 /* tag_4r4zu */ , TraceLevel.Warning, `MruItemNotFoundInStore [Action=${action.type}][ServerDocId=${serverDocId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e289862 /* tag_4kj78 */ , TraceLevel.Warning, `StoreException: MruItem already soft deleted in store [ServerDocId=${serverDocId}]`);
            return false;
        }
        const updatedMruItem = storeCopy.value.getClone().applyDiffs(delta);
        if (!isEqual(storeCopy.value, updatedMruItem)) {
            this.loggers.traceLogger.logTrace(0x1e45e657 /* tag_4r4zx */ , TraceLevel.Verbose, `Pushing delta for MruItem [ServerDocId=${serverDocId}][UpdateKey=${updateKey}][DeltaCount=${storeCopy.deltaCount}]`);
            storeCopy.pushDelta(delta, updateKey);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleUpdateMruItemFailedAction(action) {
        let storeChanged = false;
        const { serverDocId, updateKey } = action;
        const storeCopy = this.mruTable.get(serverDocId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This can happen if a local delete or delta sync delete occurs before the response of the update returns.
            // In case of mru, since delta sync is unimplemented, it can only be the former. Log a warning and no-op
            this.loggers.traceLogger.logTrace(0x1e45e656 /* tag_4r4zw */ , TraceLevel.Warning, `MruItemNotFoundInStore [ServerDocId=${serverDocId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Revert the delta if it exists
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.revertDelta(updateKey);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleUpdateMruItemSucceededAction(action) {
        let storeChanged = false;
        const { updatedMruItem, updateKey } = action;
        const storeCopy = this.mruTable.get(updatedMruItem.serverDocId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This can happen if a local delete or delta sync delete occurs before the response of the update returns.
            // In case of mru, since delta sync is unimplemented, it can only be the former. Log a warning and no-op
            this.loggers.traceLogger.logTrace(0x1e45e655 /* tag_4r4zv */ , TraceLevel.Warning, `MruItemNotFoundInStore [ServerDocId=${updatedMruItem.serverDocId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Commit the pending update
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.commitDelta(updateKey, updatedMruItem);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleDeleteMruItemAction(action) {
        const { serverDocId } = action;
        const storeCopy = this.mruTable.get(serverDocId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e45e652 /* tag_4r4zs */ , TraceLevel.Warning, `MruItemNotFoundInStore [Action=${action.type}][ServerDocId=${serverDocId}]`);
            return false;
        }
        // No need to delete if already soft deleted.
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e45e653 /* tag_4r4zt */ , TraceLevel.Warning, `StoreException: MruItem already soft deleted in store [ServerDocId=${serverDocId}]`);
            return false;
        }
        storeCopy.softDelete();
        return true;
    }
    handleDeleteMruItemFailedAction(action) {
        const { deletedMruItem } = action;
        const storeCopy = this.mruTable.get(deletedMruItem.serverDocId);
        if (!storeCopy) {
            /**
             * This can happen if delta sync delete occurs before the
             * response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e45e650 /* tag_4r4zq */ , TraceLevel.Warning, `MruItemNotFoundInStore [Action=${action.type}][ServerDocId=${deletedMruItem.serverDocId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            // Un-delete the mru item
            storeCopy.undelete();
            return true;
        } else {
            this.loggers.traceLogger.logTrace(0x1e45e651 /* tag_4r4zr */ , TraceLevel.Warning, `StoreException: MruItem deletion failure for non soft-deleted MruItem [ServerDocId=${deletedMruItem.serverDocId}]`);
            return false;
        }
    }
    handleDeleteMruItemSucceededAction(action) {
        const { serverDocId } = action;
        const storeCopy = this.mruTable.get(serverDocId);
        if (storeCopy == null) {
            // Log this case. This can happen in the rare chance if a delta sync returns before the response of the delete
            // That said, we currently do not have delta sync for MruItems implemented. So this should never happen
            this.loggers.traceLogger.logTrace(0x1e289861 /* tag_4kj77 */ , TraceLevel.Error, `MruItemNotFoundInStore [ServerDocId=${serverDocId}]`);
            return false;
        }
        // Update recently deleted items to keep track of them
        this.mruRecentlyDeletedItems.add(serverDocId);
        // Remove from the store tables
        this.mruTable.delete(serverDocId);
        return true;
    }
    constructor(loggers, config){
        super(), this.loggers = loggers, this.config = config, this.mruCollectionKey = MruStoreCollectionKey.MRU_COLLECTION, // Data Tables
        this.mruTable = new EntityTable(StoreTableNames.Mru, {
            mruId: (item)=>item.mruId,
            visible: (item)=>this.mruItemEntityFilterPredicate(item) ? "true" : undefined,
            visiblePinned: (item)=>this.mruItemEntityFilterPredicate(item) && item.isPinned ? "true" : undefined
        }, new MruSerializationHandler()), this.mruCollectionStatusTable = new BasicTable(StoreTableNames.MruCollectionStatus, {}, new StoreEntityStatusSerializationHandler()), // Data Tables for created MRU items - used to track local changes so that pinned state can be updated if fetch happens after create
        this.createdMruItems = {}, // Mru item ids of recently deleted items
        this.mruRecentlyDeletedItems = new Set(), // Table for fetching status of mru items
        this.isMruFetchingInProgress = {}, /**
     * Filter predicate for mru items entities in the mruStore
     * @param mruItem Mru item entity to filter
     */ this.mruItemEntityFilterPredicate = (mruItem)=>{
            if (mruItem.app !== MruApp.Project) {
                return true;
            }
            const metadata = mruItem.metadata;
            if (metadata.orgType === ProjectMetadataOrgType.None) {
                this.loggers.traceLogger.logTrace(0x1e30370b /* tag_4md2l */ , TraceLevel.Info, "Plan org is of type none");
            }
            if (this.config().flights.ShowNamedOrgPlansInUno) {
                if (metadata.orgType === ProjectMetadataOrgType.Named) {
                    return true;
                }
            }
            //Remove this check when we support named orgs
            //WI: 8432542
            if (metadata.orgType === ProjectMetadataOrgType.Named || metadata.orgType === ProjectMetadataOrgType.None) {
                return false;
            }
            if (mruItem.isProjectOnlinePlan()) {
                return true;
            }
            if (mruItem.isProjectOperationsPlan()) {
                return true;
            }
            return metadata.orgType === ProjectMetadataOrgType.Default;
        };
    }
}
